import { useCallback, useEffect, useState } from "react";
import { BellRing, Download, FileText, Mail, Plus, Trash2, Upload } from "lucide-react";
import { platformClient, formatPlatformError } from "./platformApi";
import { AiPolishButton } from "./AdminProposalsTab";

const inputCls =
    "mt-2 w-full border-b border-white/20 bg-transparent pb-2.5 text-sm text-bone placeholder:text-ash/50 transition-colors focus:border-gold focus:outline-none";
const labelCls = "text-[10px] uppercase tracking-[0.25em] text-ash";

const euro = (v) => `€ ${Number(v || 0).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_LABELS = { concept: "Concept", verstuurd: "Verstuurd", betaald: "Betaald", verlopen: "Verlopen", akkoord: "Akkoord", afgewezen: "Afgewezen" };
const SUB_STATUSES = { proef: "Proef", actief: "Actief", gepauzeerd: "Gepauzeerd", opgezegd: "Opgezegd" };

const downloadBlob = (data, filename, type) => {
    const url = URL.createObjectURL(new Blob([data], { type }));
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
};

export const AdminBillingTab = ({ tenants }) => {
    const [plans, setPlans] = useState(null);
    const [subs, setSubs] = useState({});
    const [invoices, setInvoices] = useState([]);
    const [clients, setClients] = useState([]);
    const [period, setPeriod] = useState(() => new Date().toISOString().slice(0, 7));
    const [notice, setNotice] = useState("");
    const [error, setError] = useState("");
    const [manual, setManual] = useState({ tenant_id: "", client_id: "", client_name: "", client_email: "", client_address: "", client_kvk: "", client_btw: "", description: "", quantity: 1, unit_price: "", vat_rate: 21 });

    const [editing, setEditing] = useState(null);
    const [setupFee, setSetupFee] = useState("");

    const pickInvoiceClient = (cid) => {
        if (!cid || cid === "__vrij__") {
            setManual((m) => ({ ...m, tenant_id: cid, client_id: "" }));
            return;
        }
        const c = clients.find((x) => x.id === cid);
        if (!c) return;
        setManual((m) => ({
            ...m,
            tenant_id: cid,
            client_id: c.id,
            client_name: c.company || "",
            client_email: c.email || "",
            client_address: c.address || "",
            client_kvk: c.kvk || "",
            client_btw: c.btw_number || "",
        }));
        flash(`Klantgegevens van ${c.company} ingevuld`);
    };

    const startEdit = () => {
        setEditing(plans.plans.map((p) => ({ ...p })));
        setSetupFee(String(plans.default_setup_fee));
    };

    const savePlans = async () => {
        setError("");
        try {
            const payload = {
                plans: editing.map((p) => ({ ...p, monthly_price: Number(p.monthly_price) || 0 })),
                default_setup_fee: Number(setupFee) || 0,
            };
            const { data } = await platformClient.put("/admin/billing/plans", payload);
            setPlans(data);
            setEditing(null);
            flash("Prijzenstaffel opgeslagen — geldt direct voor contracten, offertes en maandfacturen");
        } catch (err) {
            setError(formatPlatformError(err.response?.data?.detail));
        }
    };

    const flash = (msg) => {
        setNotice(msg);
        setTimeout(() => setNotice(""), 6000);
    };

    const load = useCallback(async () => {
        try {
            const [p, inv, ov, cl] = await Promise.all([
                platformClient.get("/admin/billing/plans"),
                platformClient.get("/admin/billing/invoices"),
                platformClient.get("/admin/billing/overview"),
                platformClient.get("/admin/clients"),
            ]);
            setPlans(p.data);
            setInvoices(inv.data);
            setClients(cl.data);
            const map = {};
            for (const t of ov.data.per_tenant) map[t.tenant_id] = t;
            setSubs(map);
        } catch {}
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const [subForm, setSubForm] = useState({});
    const subOf = (tid) =>
        subForm[tid] || {
            plan: subs[tid]?.plan || "compleet",
            monthly_price: subs[tid]?.monthly_price ?? 149,
            setup_fee: subs[tid]?.setup_fee ?? (plans?.default_setup_fee ?? 595),
            status: subs[tid]?.status || "proef",
            start_date: subs[tid]?.start_date || "",
        };
    const setSub = (tid, patch) => setSubForm((s) => ({ ...s, [tid]: { ...subOf(tid), ...patch } }));

    const saveSub = async (tid) => {
        setError("");
        try {
            await platformClient.put(`/admin/tenants/${tid}/subscription`, {
                ...subOf(tid),
                monthly_price: Number(subOf(tid).monthly_price) || 0,
                setup_fee: Number(subOf(tid).setup_fee) || 0,
            });
            flash("Abonnement opgeslagen");
            load();
        } catch (err) {
            setError(formatPlatformError(err.response?.data?.detail));
        }
    };

    const generate = async () => {
        setError("");
        try {
            const { data } = await platformClient.post("/admin/billing/generate", { period });
            flash(`${data.created} factuur/facturen aangemaakt voor ${data.period} (${data.skipped} overgeslagen — bestonden al)`);
            load();
        } catch (err) {
            setError(formatPlatformError(err.response?.data?.detail));
        }
    };

    const downloadPdf = async (inv) => {
        const { data } = await platformClient.get(`/admin/billing/invoices/${inv.id}/pdf`, { responseType: "blob" });
        downloadBlob(data, `${inv.number}.pdf`, "application/pdf");
    };

    const downloadWord = async (inv) => {
        const { data } = await platformClient.get(`/admin/billing/invoices/${inv.id}/docx`, { responseType: "blob" });
        downloadBlob(data, `${inv.number}.docx`, "application/vnd.openxmlformats-officedocument.wordprocessingml.document");
    };

    const uploadWord = async (inv, file) => {
        if (!file) return;
        setError("");
        try {
            const fd = new FormData();
            fd.append("file", file);
            await platformClient.post(`/admin/billing/invoices/${inv.id}/docx-upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            flash(`Word-versie van ${inv.number} opgeslagen als definitieve PDF`);
            load();
        } catch (err) {
            setError(formatPlatformError(err.response?.data?.detail));
        }
    };

    const revertCustom = async (inv) => {
        await platformClient.delete(`/admin/billing/invoices/${inv.id}/custom-pdf`);
        flash(`Factuur ${inv.number} terug naar platform-versie`);
        load();
    };

    const [contractBusy, setContractBusy] = useState(null);

    const downloadContract = async (t) => {
        setError("");
        setContractBusy(t.id);
        try {
            // Eerst de ingevulde bedragen opslaan — het contract volgt altijd het scherm
            await platformClient.put(`/admin/tenants/${t.id}/subscription`, {
                ...subOf(t.id),
                monthly_price: Number(subOf(t.id).monthly_price) || 0,
                setup_fee: Number(subOf(t.id).setup_fee) || 0,
            });
            const { data } = await platformClient.get(`/admin/tenants/${t.id}/contract.pdf`, { responseType: "blob" });
            downloadBlob(data, `saas-overeenkomst-${t.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}.pdf`, "application/pdf");
            load();
        } catch (err) {
            setError(formatPlatformError(err.response?.data?.detail));
        } finally {
            setContractBusy(null);
        }
    };

    const makeQuote = async (t) => {
        setError("");
        const s = subOf(t.id);
        const planLabel = (plans?.plans || []).find((p) => p.id === s.plan)?.label || s.plan;
        const items = [];
        if (Number(s.setup_fee) > 0) {
            items.push({ description: "Onboarding — inrichting, data-import en training (eenmalig)", quantity: 1, unit_price: Number(s.setup_fee) });
        }
        items.push({ description: `Licentie ${planLabel} — eerste maand`, quantity: 1, unit_price: Number(s.monthly_price) || 0 });
        try {
            await platformClient.post("/admin/billing/invoices", {
                tenant_id: t.id, doc_type: "offerte", items,
                plan: s.plan, monthly_price: Number(s.monthly_price) || 0, setup_fee: Number(s.setup_fee) || 0,
            });
            flash(`Verkoopofferte voor ${t.name} aangemaakt — verstuur hem via de e-mailknop in de lijst`);
            load();
        } catch (err) {
            setError(formatPlatformError(err.response?.data?.detail));
        }
    };

    const remindInvoice = async (inv) => {
        setError("");
        try {
            const { data } = await platformClient.post(`/admin/billing/invoices/${inv.id}/reminder`);
            flash(`Herinnering voor ${inv.number} verstuurd naar ${data.to}`);
            load();
        } catch (err) {
            setError(formatPlatformError(err.response?.data?.detail));
        }
    };

    const emailInvoice = async (inv) => {
        setError("");
        try {
            const { data } = await platformClient.post(`/admin/billing/invoices/${inv.id}/email`);
            flash(`Factuur ${inv.number} verstuurd naar ${data.to}`);
            load();
        } catch (err) {
            setError(formatPlatformError(err.response?.data?.detail));
        }
    };

    const setStatus = async (inv, status) => {
        try {
            await platformClient.patch(`/admin/billing/invoices/${inv.id}`, { status });
            load();
        } catch {}
    };

    const removeInvoice = async (inv) => {
        if (!window.confirm(`Factuur ${inv.number} verwijderen?`)) return;
        try {
            await platformClient.delete(`/admin/billing/invoices/${inv.id}`);
            load();
        } catch (err) {
            setError(formatPlatformError(err.response?.data?.detail));
        }
    };

    const createManual = async (e) => {
        e.preventDefault();
        setError("");
        try {
            await platformClient.post("/admin/billing/invoices", {
                tenant_id: manual.tenant_id.startsWith("t-") ? manual.tenant_id : "",
                client_id: manual.client_id,
                client_name: manual.client_name,
                client_email: manual.client_email,
                client_address: manual.client_address,
                client_kvk: manual.client_kvk,
                client_btw: manual.client_btw,
                items: [{ description: manual.description, quantity: Number(manual.quantity) || 1, unit_price: Number(manual.unit_price) || 0 }],
                vat_rate: Number(manual.vat_rate),
            });
            setManual({ tenant_id: "", client_id: "", client_name: "", client_email: "", client_address: "", client_kvk: "", client_btw: "", description: "", quantity: 1, unit_price: "", vat_rate: 21 });
            flash("Factuur aangemaakt");
            load();
        } catch (err) {
            setError(formatPlatformError(err.response?.data?.detail));
        }
    };

    return (
        <div data-testid="admin-billing-tab" className="mt-8">
            {plans ? (
                <div>
                    <div className="flex items-center justify-between">
                        <p className={labelCls}>Prijzenstaffel</p>
                        {!editing ? (
                            <button
                                data-testid="plans-edit-button"
                                onClick={startEdit}
                                className="border border-white/15 px-4 py-2 text-[10px] uppercase tracking-[0.15em] text-ash transition-colors hover:border-gold/60 hover:text-gold"
                            >
                                Staffel aanpassen
                            </button>
                        ) : null}
                    </div>
                    {!editing ? (
                        <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                            {plans.plans.map((p) => (
                                <div key={p.id} data-testid={`plan-card-${p.id}`} className="border border-white/10 bg-coal p-6">
                                    <p className={labelCls}>{p.label}</p>
                                    <p className="mt-3 font-display text-3xl text-gold">{p.monthly_price ? euro(p.monthly_price) : "—"}</p>
                                    <p className="mt-2 text-xs text-ash">{p.desc}</p>
                                </div>
                            ))}
                            <div className="border border-gold/30 bg-gold/5 p-6 md:col-span-2 xl:col-span-4">
                                <p className="text-xs text-ash">
                                    Onboarding (eenmalig): <span className="text-gold">{euro(plans.default_setup_fee)}</span> — inrichting, data-import en training.
                                    Jaarlijks vooruitbetaald: <span className="text-gold">10% korting</span>. Pas per klant aan bij het abonnement hieronder; het contract en de offerte nemen deze waarden over.
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div data-testid="plans-editor" className="mt-3 border border-white/10 bg-coal p-6">
                            <div className="space-y-4">
                                {editing.map((p, i) => (
                                    <div key={i} data-testid={`plan-edit-row-${i}`} className="flex flex-wrap items-end gap-4 border-b border-white/5 pb-4">
                                        <label className="block">
                                            <span className={labelCls}>Naam</span>
                                            <input
                                                data-testid={`plan-edit-label-${i}`}
                                                value={p.label}
                                                onChange={(e) => setEditing(editing.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
                                                className={`${inputCls} w-44`}
                                            />
                                        </label>
                                        <label className="block">
                                            <span className={labelCls}>€ / maand</span>
                                            <input
                                                data-testid={`plan-edit-price-${i}`}
                                                type="number" min="0" step="any"
                                                value={p.monthly_price}
                                                onChange={(e) => setEditing(editing.map((x, j) => (j === i ? { ...x, monthly_price: e.target.value } : x)))}
                                                className={`${inputCls} w-24`}
                                            />
                                        </label>
                                        <label className="block">
                                            <span className={labelCls}>Omschrijving</span>
                                            <input
                                                data-testid={`plan-edit-desc-${i}`}
                                                value={p.desc}
                                                onChange={(e) => setEditing(editing.map((x, j) => (j === i ? { ...x, desc: e.target.value } : x)))}
                                                className={`${inputCls} w-64`}
                                            />
                                        </label>
                                        {p.id !== "compleet" ? (
                                            <button
                                                data-testid={`plan-edit-remove-${i}`}
                                                onClick={() => setEditing(editing.filter((_, j) => j !== i))}
                                                aria-label="Plan verwijderen"
                                                className="pb-2 text-ash/50 transition-colors hover:text-red-400"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        ) : (
                                            <span className="pb-2 text-[10px] uppercase tracking-[0.15em] text-ash/50" title="Dit plan activeert alle modules bij offerte-akkoord">verplicht</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                            <div className="mt-5 flex flex-wrap items-end gap-4">
                                <button
                                    data-testid="plan-add-button"
                                    onClick={() => setEditing([...editing, { id: `plan_${Date.now().toString(36)}`, label: "", monthly_price: 0, desc: "" }])}
                                    className="flex items-center gap-2 border border-white/15 px-4 py-2.5 text-[10px] uppercase tracking-[0.15em] text-ash transition-colors hover:border-gold/60 hover:text-gold"
                                >
                                    <Plus className="h-3.5 w-3.5" /> Plan toevoegen
                                </button>
                                <label className="block">
                                    <span className={labelCls}>Onboarding eenmalig €</span>
                                    <input
                                        data-testid="plan-edit-setupfee"
                                        type="number" min="0" step="any"
                                        value={setupFee}
                                        onChange={(e) => setSetupFee(e.target.value)}
                                        className={`${inputCls} w-28`}
                                    />
                                </label>
                                <button
                                    data-testid="plans-save-button"
                                    onClick={savePlans}
                                    className="bg-gold px-5 py-2.5 text-[10px] uppercase tracking-[0.2em] text-black transition-colors hover:bg-bone"
                                >
                                    Staffel opslaan
                                </button>
                                <button
                                    data-testid="plans-cancel-button"
                                    onClick={() => setEditing(null)}
                                    className="px-4 py-2.5 text-[10px] uppercase tracking-[0.15em] text-ash transition-colors hover:text-bone"
                                >
                                    Annuleer
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            ) : null}

            <h3 className="mt-10 font-display text-2xl tracking-tight">Abonnementen per klant</h3>
            <div className="mt-4 space-y-3">
                {tenants.map((t) => {
                    const s = subOf(t.id);
                    return (
                        <div key={t.id} data-testid={`sub-row-${t.id}`} className="border border-white/10 bg-coal p-5">
                            <div className="flex flex-wrap items-end gap-4">
                                <div className="min-w-40">
                                    <p className="text-sm text-bone">{t.name}</p>
                                    <p className="text-[10px] uppercase tracking-[0.2em] text-ash">{SUB_STATUSES[subs[t.id]?.status] || "Geen abonnement"}</p>
                                </div>
                                <label className="block">
                                    <span className={labelCls}>Plan</span>
                                    <select
                                        data-testid={`sub-plan-${t.id}`}
                                        value={s.plan}
                                        onChange={(e) => {
                                            const plan = (plans?.plans || []).find((p) => p.id === e.target.value);
                                            setSub(t.id, { plan: e.target.value, ...(plan?.monthly_price ? { monthly_price: plan.monthly_price } : {}) });
                                        }}
                                        className="mt-2 border-b border-white/20 bg-ink pb-2 text-sm text-bone focus:border-gold focus:outline-none"
                                    >
                                        {(plans?.plans || []).map((p) => (
                                            <option key={p.id} value={p.id}>{p.label}</option>
                                        ))}
                                    </select>
                                </label>
                                <label className="block">
                                    <span className={labelCls}>€ / maand</span>
                                    <input
                                        data-testid={`sub-price-${t.id}`}
                                        type="number" min="0" step="any"
                                        value={s.monthly_price}
                                        onChange={(e) => setSub(t.id, { monthly_price: e.target.value })}
                                        className={`${inputCls} w-24`}
                                    />
                                </label>
                                <label className="block">
                                    <span className={labelCls}>Setup €</span>
                                    <input
                                        data-testid={`sub-setup-${t.id}`}
                                        type="number" min="0" step="any"
                                        value={s.setup_fee}
                                        onChange={(e) => setSub(t.id, { setup_fee: e.target.value })}
                                        className={`${inputCls} w-24`}
                                    />
                                </label>
                                <label className="block">
                                    <span className={labelCls}>Status</span>
                                    <select
                                        data-testid={`sub-status-${t.id}`}
                                        value={s.status}
                                        onChange={(e) => setSub(t.id, { status: e.target.value })}
                                        className="mt-2 border-b border-white/20 bg-ink pb-2 text-sm text-bone focus:border-gold focus:outline-none"
                                    >
                                        {Object.entries(SUB_STATUSES).map(([id, l]) => (
                                            <option key={id} value={id}>{l}</option>
                                        ))}
                                    </select>
                                </label>
                                <button
                                    data-testid={`sub-save-${t.id}`}
                                    onClick={() => saveSub(t.id)}
                                    className="border border-gold/50 px-4 py-2.5 text-[10px] uppercase tracking-[0.15em] text-gold transition-colors hover:bg-gold hover:text-black"
                                >
                                    Opslaan
                                </button>
                                <button
                                    data-testid={`sub-quote-${t.id}`}
                                    onClick={() => makeQuote(t)}
                                    title="Maak een verkoopofferte met deze prijzen — bij akkoord wordt het abonnement actief"
                                    className="flex items-center gap-2 border border-white/15 px-4 py-2.5 text-[10px] uppercase tracking-[0.15em] text-ash transition-colors hover:border-gold/60 hover:text-gold"
                                >
                                    <Mail className="h-3.5 w-3.5" /> Maak offerte
                                </button>
                                <button
                                    data-testid={`sub-contract-${t.id}`}
                                    onClick={() => downloadContract(t)}
                                    disabled={contractBusy === t.id}
                                    title="Slaat de ingevulde bedragen op en downloadt de SaaS-overeenkomst — het contract volgt altijd wat je ziet"
                                    className="flex items-center gap-2 border border-white/15 px-4 py-2.5 text-[10px] uppercase tracking-[0.15em] text-ash transition-colors hover:border-gold/60 hover:text-gold disabled:cursor-wait disabled:opacity-40"
                                >
                                    <FileText className="h-3.5 w-3.5" /> {contractBusy === t.id ? "Bezig…" : "Contract PDF"}
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="mt-10 flex flex-wrap items-end justify-between gap-4">
                <h3 className="font-display text-2xl tracking-tight">Platform-facturen ({invoices.length})</h3>
                <div className="flex items-end gap-3">
                    <label className="block">
                        <span className={labelCls}>Periode</span>
                        <input
                            data-testid="generate-period"
                            type="month"
                            value={period}
                            onChange={(e) => setPeriod(e.target.value)}
                            className={`${inputCls} [color-scheme:dark]`}
                        />
                    </label>
                    <button
                        data-testid="generate-monthly-button"
                        onClick={generate}
                        className="flex items-center gap-2 bg-gold px-5 py-3 text-xs uppercase tracking-[0.2em] text-black transition-colors hover:bg-bone"
                    >
                        <Plus className="h-4 w-4" /> Maandfacturen genereren
                    </button>
                </div>
            </div>

            <form data-testid="manual-invoice-form" onSubmit={createManual} className="mt-6 grid grid-cols-1 gap-4 border border-white/10 bg-coal p-5 sm:grid-cols-6">
                <label className="block sm:col-span-2">
                    <span className={labelCls}>Klant</span>
                    <select
                        data-testid="manual-invoice-tenant"
                        value={manual.tenant_id}
                        onChange={(e) => pickInvoiceClient(e.target.value)}
                        required
                        className="mt-2 w-full border-b border-white/20 bg-ink pb-2 text-sm text-bone focus:border-gold focus:outline-none [&>option]:bg-coal [&>option]:text-bone"
                    >
                        <option value="">— kies klant —</option>
                        <option value="__vrij__">Vrije klant (handmatig invoeren)</option>
                        {clients.map((c) => (
                            <option key={c.id} value={c.id}>{c.company}{c.contact_name ? ` — ${c.contact_name}` : ""}</option>
                        ))}
                        {tenants.map((t) => (
                            <option key={t.id} value={t.id}>{t.name} (tenant)</option>
                        ))}
                    </select>
                </label>
                {manual.tenant_id === "__vrij__" ? (
                    <>
                        <label className="block sm:col-span-2">
                            <span className={labelCls}>Klantnaam</span>
                            <input
                                data-testid="manual-invoice-client-name"
                                value={manual.client_name}
                                onChange={(e) => setManual({ ...manual, client_name: e.target.value })}
                                required
                                className={inputCls}
                            />
                        </label>
                        <label className="block sm:col-span-2">
                            <span className={labelCls}>Klant e-mail (voor verzending)</span>
                            <input
                                data-testid="manual-invoice-client-email"
                                type="email"
                                value={manual.client_email}
                                onChange={(e) => setManual({ ...manual, client_email: e.target.value })}
                                className={inputCls}
                            />
                        </label>
                        <label className="block sm:col-span-2">
                            <span className={labelCls}>Klantadres (regels met enter)</span>
                            <textarea
                                data-testid="manual-invoice-client-address"
                                rows={2}
                                value={manual.client_address}
                                onChange={(e) => setManual({ ...manual, client_address: e.target.value })}
                                className={inputCls}
                            />
                        </label>
                    </>
                ) : null}
                <label className="block sm:col-span-2">
                    <span className={labelCls}>Omschrijving (bijv. onboarding)</span>
                    <input
                        data-testid="manual-invoice-desc"
                        value={manual.description}
                        onChange={(e) => setManual({ ...manual, description: e.target.value })}
                        required
                        className={inputCls}
                    />
                    <span className="mt-1 block">
                        <AiPolishButton testid="manual-invoice-desc-ai" text={manual.description} context="factuur omschrijving" onApply={(v) => setManual((m) => ({ ...m, description: v }))} />
                    </span>
                </label>
                <label className="block">
                    <span className={labelCls}>Bedrag €</span>
                    <input
                        data-testid="manual-invoice-price"
                        type="number" min="0" step="any" required
                        value={manual.unit_price}
                        onChange={(e) => setManual({ ...manual, unit_price: e.target.value })}
                        className={inputCls}
                    />
                </label>
                <div className="flex items-end">
                    <button
                        data-testid="manual-invoice-submit"
                        type="submit"
                        className="w-full border border-gold/50 px-4 py-2.5 text-[10px] uppercase tracking-[0.15em] text-gold transition-colors hover:bg-gold hover:text-black"
                    >
                        Factuur aanmaken
                    </button>
                </div>
            </form>

            {error ? (
                <p data-testid="billing-error" className="mt-4 border border-red-400/30 bg-red-400/5 px-4 py-3 text-xs text-red-300">{error}</p>
            ) : null}
            {notice ? (
                <p data-testid="billing-notice" className="mt-4 border border-gold/30 bg-gold/5 px-4 py-3 text-xs uppercase tracking-[0.15em] text-gold">{notice}</p>
            ) : null}

            <div className="mt-6 space-y-3">
                {invoices.map((inv) => (
                    <div key={inv.id} data-testid={`pinv-row-${inv.id}`} className="flex flex-wrap items-center justify-between gap-3 border border-white/10 bg-coal px-5 py-4">
                        <div>
                            <p className="text-sm text-bone">
                                <span className="text-gold">{inv.number}</span> — {inv.tenant_name}
                                <span className={`ml-2 border px-2 py-0.5 text-[9px] uppercase tracking-[0.15em] ${inv.doc_type === "offerte" ? "border-gold/40 text-gold" : "border-white/15 text-ash"}`}>
                                    {inv.doc_type === "offerte" ? "Offerte" : "Factuur"}
                                </span>
                            </p>
                            <p className="mt-1 text-xs text-ash">
                                {inv.issue_date}{inv.period ? ` · periode ${inv.period}` : ""} · <span className="text-bone">{euro(inv.total)}</span>
                                {inv.reminded_at ? <span className="text-ash/60"> · herinnerd {inv.reminded_at.slice(0, 10)}</span> : null}
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            <select
                                data-testid={`pinv-status-${inv.id}`}
                                value={inv.status}
                                onChange={(e) => setStatus(inv, e.target.value)}
                                className="border border-white/15 bg-ink px-3 py-2 text-xs uppercase tracking-[0.15em] text-bone focus:border-gold focus:outline-none"
                            >
                                {Object.entries(STATUS_LABELS).map(([id, l]) => (
                                    <option key={id} value={id}>{l}</option>
                                ))}
                            </select>
                            <button data-testid={`pinv-pdf-${inv.id}`} onClick={() => downloadPdf(inv)} aria-label="PDF downloaden" className="border border-white/15 p-2.5 text-ash transition-colors hover:border-gold/60 hover:text-gold">
                                <Download className="h-4 w-4" />
                            </button>
                            <button data-testid={`pinv-word-${inv.id}`} onClick={() => downloadWord(inv)} aria-label="Word downloaden" title="Download als Word om zelf te bewerken" className="border border-white/15 p-2.5 text-ash transition-colors hover:border-gold/60 hover:text-gold">
                                <FileText className="h-4 w-4" />
                            </button>
                            <label data-testid={`pinv-upload-${inv.id}`} title="Upload je bewerkte Word-versie (wordt de definitieve PDF)" className="cursor-pointer border border-white/15 p-2.5 text-ash transition-colors hover:border-gold/60 hover:text-gold">
                                <Upload className="h-4 w-4" />
                                <input type="file" accept=".docx" className="hidden" onChange={(e) => uploadWord(inv, e.target.files[0])} />
                            </label>
                            {inv.custom_pdf_at ? (
                                <button data-testid={`pinv-revert-${inv.id}`} onClick={() => revertCustom(inv)} title="Terug naar platform-versie" className="border border-gold/40 px-2 py-2 text-[9px] uppercase tracking-wide text-gold hover:bg-gold/10">
                                    Eigen versie ✕
                                </button>
                            ) : null}
                            <button data-testid={`pinv-email-${inv.id}`} onClick={() => emailInvoice(inv)} aria-label={inv.doc_type === "offerte" ? "Offerte versturen" : "E-mail versturen"} title={inv.doc_type === "offerte" ? "Verstuur offerte met online-akkoord link" : "Verstuur factuur per e-mail"} className="border border-white/15 p-2.5 text-ash transition-colors hover:border-gold/60 hover:text-gold">
                                <Mail className="h-4 w-4" />
                            </button>
                            {inv.doc_type !== "offerte" && ["verstuurd", "verlopen"].includes(inv.status) ? (
                                <button data-testid={`pinv-remind-${inv.id}`} onClick={() => remindInvoice(inv)} aria-label="Herinnering sturen" title="Stuur betalingsherinnering" className="border border-white/15 p-2.5 text-ash transition-colors hover:border-gold/60 hover:text-gold">
                                    <BellRing className="h-4 w-4" />
                                </button>
                            ) : null}
                            {inv.status === "concept" ? (
                                <button data-testid={`pinv-delete-${inv.id}`} onClick={() => removeInvoice(inv)} aria-label="Verwijderen" className="border border-white/15 p-2.5 text-ash transition-colors hover:border-red-400/60 hover:text-red-400">
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            ) : null}
                        </div>
                    </div>
                ))}
                {invoices.length === 0 ? (
                    <p data-testid="billing-empty" className="text-sm text-ash">Nog geen platform-facturen. Stel een abonnement in en genereer de maandfacturen.</p>
                ) : null}
            </div>
        </div>
    );
};
